import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import {
  Calendar,
  Document,
  Eye,
  Clock,
  Employees,
  Check,
} from "@/components/icons";
import { formatDate } from "@/utils";
import type { ExamDetail } from "../../types/exam-management.types";
import { examManagementApi } from "../../services/exam-management.api";
import styles from "./ExamDetailDrawer.module.scss";

interface ExamDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  examId: string | null;
}

const ExamDetailDrawer: React.FC<ExamDetailDrawerProps> = ({
  isOpen,
  onClose,
  examId,
}) => {
  const { t } = useTranslation();
  const [exam, setExam] = useState<ExamDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setExam(null);
      return;
    }
    if (!examId) {
      setExam(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    examManagementApi
      .getDetailById(examId)
      .then((data) => {
        if (!cancelled) setExam(data);
      })
      .catch(() => {
        if (!cancelled) setExam(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, examId]);

  const formatDuration = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours} saat ${mins} dk` : `${hours} saat`;
    }
    return `${minutes} dk`;
  };

  if (!isOpen) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      headerIcon={<Eye width={24} height={24} color="#14181F" />}
      title={t("examManagement.detail.title")}
      position="right"
      width="640px"
    >
      <div className={styles.drawerContent}>
        {loading && (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        )}
        {!loading && !exam && (
          <div className={styles.error}>
            {t("examManagement.detail.notFound")}
          </div>
        )}
        {!loading && exam && (
          <>
            <h4 className={styles.examName}>{exam.name}</h4>

            <div className={styles.infoCard}>
              <div className={styles.detailList}>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>
                    <Calendar width={20} height={20} />
                  </span>
                  <span className={styles.detailLabel}>
                    {t("examManagement.detail.publishDate")}
                  </span>
                  <span className={styles.detailValue}>{formatDate(exam.createdAt)}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>
                    <Document width={20} height={20} />
                  </span>
                  <span className={styles.detailLabel}>
                    {t("examManagement.detail.questionCount")}
                  </span>
                  <span className={styles.detailValue}>
                    {exam.questions.length}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>
                    <Clock width={20} height={20} />
                  </span>
                  <span className={styles.detailLabel}>
                    {t("examManagement.detail.duration")}
                  </span>
                  <span className={styles.detailValue}>
                    {formatDuration(exam.durationMinutes)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>
                    <Employees width={20} height={20} />
                  </span>
                  <span className={styles.detailLabel}>
                    {t("examManagement.detail.passingScore")}
                  </span>
                  <span className={styles.detailValue}>
                    {exam.passingScore}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>
                    <Employees width={20} height={20} />
                  </span>
                  <span className={styles.detailLabel}>
                    {t("examManagement.detail.successRate")}
                  </span>
                  <span className={styles.detailValue}>
                    %{exam.successRate ?? 0} ({exam.passedCount}/{exam.totalAttempts})
                  </span>
                </div>
                {exam.relatedEducations?.length > 0 && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Document width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("examManagement.detail.relatedEducation")}
                    </span>
                    <span className={styles.detailValue}>
                      {exam.relatedEducations.map((e) => e.name).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {exam.description && (
              <section className={styles.descriptionSection}>
                <h4 className={styles.descriptionTitle}>
                  {t("examManagement.detail.description")}
                </h4>
                <p className={styles.examDescription}>{exam.description}</p>
              </section>
            )}

            <div className={styles.questionsSection}>
              <h4 className={styles.questionsSectionTitle}>
                {t("examManagement.detail.questions")}
              </h4>
              <div className={styles.questionsWrapper}>
                {exam.questions
                  .sort((a, b) => a.order - b.order)
                  .map((question, index) => (
                    <div key={question.id} className={styles.questionCard}>
                      <div className={styles.questionHeader}>
                        <span className={styles.questionNumber}>
                          {index + 1}
                        </span>
                        <p className={styles.questionText}>
                          {question.questionText}
                        </p>
                      </div>

                      <div className={styles.questionMeta}>
                        {question.allowMultipleSelection && (
                          <span className={styles.metaBadge}>
                            {t("examManagement.detail.multipleSelection")}
                          </span>
                        )}
                      </div>

                      <div className={styles.optionsList}>
                        {question.options
                          .sort((a, b) => a.order - b.order)
                          .map((option) => (
                            <div
                              key={option.id}
                              className={`${styles.optionItem} ${option.isCorrect ? styles.optionCorrect : ""}`}
                            >
                              <span
                                className={
                                  question.allowMultipleSelection
                                    ? styles.optionBulletCheckbox
                                    : styles.optionBullet
                                }
                              />
                              <span>{option.optionText}</span>
                              {option.isCorrect && (
                                <Check
                                  width={16}
                                  height={16}
                                  className={styles.correctIcon}
                                />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ExamDetailDrawer;
